import PropTypes from "prop-types";
import React, { useCallback, useEffect, useState } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { useAsyncPaginate, useComponents } from "react-select-async-paginate";
import { NOOP_FALSE } from "~/constants/callbacks";
import { BLANK_OPTION } from "~/constants/dropdowns/BlankOption";
import { baseSelectPropTypes } from "~/types/selectProps";
import Control from "./components/Control";
import Option from "./components/Option";
import { styles } from "./components/styles";
import { useSelectColor } from "./hooks/useColor";
import {
  createLoadOptionsFunction,
  fetchSelectedOptions,
} from "./services/optionsService";

const CREATING_OPTION_VALUE = "__creating__";
const INPUT_REJECTION_MULTIPLIER = 2;

const AsyncSelect = ({
  id = "",
  value = "",
  name = "dropdown",
  placeholder = "Select an Option",
  onChange = NOOP_FALSE,
  isMulti = false,
  isCreatable = false,
  isClearable = false,
  isInvalid = false,
  isSearchable = true,
  isDisabled = false,
  addBlank = false,
  tooltipText = "",
  tooltipPlacement = "bottom",
  tooltipClassName = "",
  resourcePath = "",
  formatOption,
  perPage = 50,
  debounceTimeout = 500,
  additionalParams = null,
  onCreateOption,
  formatCreateLabel,
  isValidNewOption,
  createOptionPosition = "last",
  maxInputLength,
  noOptionsMessage,
  components: customComponents = {},
  ...restProps
}) => {
  const Component = isCreatable ? CreatableSelect : Select;

  const [selectedOptions, setSelectedOptions] = useState(isMulti ? [] : null);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const color = useSelectColor();

  useEffect(() => {
    const loadInitialOptions = async () => {
      if (!value || !resourcePath) {
        setSelectedOptions(isMulti ? [] : null);
        return;
      }

      setIsInitialLoading(true);

      try {
        const valueIds = Array.isArray(value)
          ? value.map((v) => (typeof v === "object" ? v.value : v))
          : [typeof value === "object" ? value.value : value];

        const blankValues = valueIds.filter(
          (val) => val === BLANK_OPTION.value,
        );
        const nonBlankValues = valueIds.filter(
          (val) => val !== BLANK_OPTION.value,
        );

        const fetchedOptions =
          nonBlankValues.length > 0
            ? await fetchSelectedOptions(
                resourcePath,
                nonBlankValues,
                formatOption,
              )
            : [];

        const blankOptions = blankValues.map(() => BLANK_OPTION);
        const allOptions = [...blankOptions, ...fetchedOptions];

        setSelectedOptions(isMulti ? allOptions : allOptions[0] || null);

        // Clear orphaned value when entity was deleted (not found in API)
        if (
          nonBlankValues.length > 0 &&
          fetchedOptions.length === 0 &&
          onChange
        ) {
          onChange(isMulti ? [] : null);
        }
      } catch (error) {
        console.warn(
          `Error loading initial selection from ${resourcePath}:`,
          error,
        );
      } finally {
        setIsInitialLoading(false);
      }
    };

    loadInitialOptions();
  }, [resourcePath]);

  const loadOptions = useCallback(
    async (search, prevLoadedOptions, { page }) => {
      const baseLoadOptions = createLoadOptionsFunction(resourcePath, {
        formatOption,
        perPage,
        additionalParams:
          additionalParams ||
          ((searchTerm) => ({ search: searchTerm || undefined })),
      });

      const result = await baseLoadOptions(search, prevLoadedOptions, { page });

      return {
        ...result,
        options:
          addBlank && page === 1
            ? [BLANK_OPTION, ...result.options]
            : result.options,
      };
    },
    [resourcePath, formatOption, perPage, additionalParams, addBlank],
  );

  const { isLoading: isAsyncLoading, ...asyncPaginateProps } = useAsyncPaginate(
    {
      loadOptions,
      defaultOptions: true,
      additional: { page: 1 },
      debounceTimeout,
    },
  );

  const defaultComponents = {
    Option,
    Control,
  };

  const components = useComponents({
    ...defaultComponents,
    ...customComponents,
  });

  const handleChange = useCallback(
    (selected) => {
      setSelectedOptions(selected);
      onChange(selected);
    },
    [onChange],
  );

  const handleCreateOption = useCallback(
    async (inputValue) => {
      if (!onCreateOption || isCreating) {
        return;
      }

      setIsCreating(true);
      const tempOption = {
        value: CREATING_OPTION_VALUE,
        label: inputValue,
        isDisabled: true,
      };
      setSelectedOptions(tempOption);

      try {
        const newOption = await onCreateOption(inputValue);

        if (newOption) {
          setSelectedOptions(newOption);
          onChange(newOption);
        } else {
          setSelectedOptions(null);
        }
      } catch (_error) {
        setSelectedOptions(null);
      } finally {
        setIsCreating(false);
      }
    },
    [onCreateOption, onChange, isCreating],
  );

  // Enforce max input length by truncating input beyond the limit
  const { onInputChange: asyncOnInputChange, ...restAsyncProps } =
    asyncPaginateProps;
  const handleInputChange = useCallback(
    (newValue, actionMeta) => {
      if (maxInputLength && actionMeta.action === "input-change") {
        if (newValue.length > maxInputLength * INPUT_REJECTION_MULTIPLIER) {
          return asyncOnInputChange("", actionMeta);
        }
        if (newValue.length > maxInputLength) {
          return asyncOnInputChange(
            newValue.slice(0, maxInputLength),
            actionMeta,
          );
        }
      }
      return asyncOnInputChange(newValue, actionMeta);
    },
    [asyncOnInputChange, maxInputLength],
  );

  const creatableProps = isCreatable
    ? {
        onCreateOption: onCreateOption ? handleCreateOption : undefined,
        formatCreateLabel,
        isValidNewOption,
        createOptionPosition,
      }
    : {};

  return (
    <Component
      inputId={id}
      name={name}
      placeholder={placeholder}
      onChange={handleChange}
      value={selectedOptions}
      isMulti={isMulti}
      closeMenuOnSelect={!isMulti}
      isClearable={isClearable}
      isSearchable={isSearchable}
      isDisabled={isDisabled || isCreating}
      isLoading={isInitialLoading || isAsyncLoading || isCreating}
      components={components}
      styles={styles(color, isInvalid)}
      tooltipText={tooltipText}
      tooltipPlacement={tooltipPlacement}
      tooltipClassName={tooltipClassName}
      noOptionsMessage={noOptionsMessage}
      onInputChange={handleInputChange}
      {...restProps}
      {...restAsyncProps}
      {...creatableProps}
    />
  );
};

AsyncSelect.propTypes = {
  ...baseSelectPropTypes,
  resourcePath: PropTypes.string,
  formatOption: PropTypes.func,
  perPage: PropTypes.number,
  debounceTimeout: PropTypes.number,
  additionalParams: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.func]),
  isCreatable: PropTypes.bool,
  onCreateOption: PropTypes.func,
  formatCreateLabel: PropTypes.func,
  isValidNewOption: PropTypes.func,
  createOptionPosition: PropTypes.oneOf(["first", "last"]),
  maxInputLength: PropTypes.number,
  noOptionsMessage: PropTypes.func,
};

export default AsyncSelect;
